import { buildHref } from "@softmax/web-addresses";
import { StoreColumns } from "@softmax/product-ux/catalog";
import * as stylex from "@stylexjs/stylex";
import {
  searchStringOf,
  type FrontDoorData,
  type PreviewSearch,
} from "../front-door/front-door";
import {
  EditBar,
  EditableBeatsList,
  EditModeProvider,
} from "../../store-editor";

import { previewGameJsonLd } from "../content/preview-content";
import { StoreBelowFoldContent } from "./below-fold";
import { PreviewStorefront } from "./preview-storefront";
import { StoreDetails } from "./store-details";
import { StoreLinks } from "./store-links";
import { StoreHeroSplit } from "./store-hero-split";
import { LeaguesBlock } from "./leagues-block";
import { RecentRoundsBlock } from "./recent-rounds-block";
type StoreLayout = "template" | "split";
export function StoreMainPage({
  address,
  content,
  isMember,
  inProgress,
  doorHref,
  canEdit,
  canReview,
  search,
  layout,
  refreshPublished,
}: FrontDoorData & {
  search: PreviewSearch;
  layout: StoreLayout;
  refreshPublished: () => Promise<void>;
}) {
  const initialEditing = canEdit && search.edit === "1";
  const rawFig = typeof search.fig === "string" ? search.fig : search.fig?.[0];
  const fig: "a" | "b" | undefined = rawFig === "a" ? "a" : undefined;
  const hero =
    layout === "template"
      ? { content, isMember, inProgress, doorHref }
      : {
          content,
          isMember,
          inProgress,
          doorHref: inProgress
            ? doorHref
            : buildHref({
                name: "game.play",
                ...content.route,
                search: new URLSearchParams(searchStringOf(search)),
              }),
          memberLabel: "Play",
        };
  return (
    <EditModeProvider
      refreshPublished={refreshPublished}
      slug={address.corpus}
      canEdit={canEdit}
      canReview={canReview}
      initialEditing={initialEditing}
    >
      <EditBar coworldName={content.coworldName} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(previewGameJsonLd(content)).replace(
            /</g,
            "\\u003c",
          ),
        }}
      />
      <main
        id="main"
        tabIndex={-1}
        {...stylex.props(layout === "split" && styles.pillBand)}
      >
        {layout === "template" && <PreviewStorefront {...hero} />}
        {layout === "split" && <StoreHeroSplit {...hero} />}
        <StoreColumns
          coworldName={content.coworldName}
          spacing={layout === "split" ? "wide" : "standard"}
          main={
            <>
              <StoreBelowFoldContent
                content={content}
                beatsSlot={
                  <EditableBeatsList liveBeats={content.copy.play.beats} />
                }
                fig={fig}
              />
            </>
          }
          aside={
            <>
              <StoreDetails content={content} sentenceCaseLabel />
              <StoreLinks content={content} />
              <LeaguesBlock content={content} />
              <RecentRoundsBlock content={content} />
            </>
          }
        />
      </main>
    </EditModeProvider>
  );
}
const phoneBreakpoint = "@media (max-width: 640px)";
const styles = stylex.create({
  pillBand: {
    paddingTop: {
      default: "calc(60px + var(--shell-nav-extra-height, 0px))",
      [phoneBreakpoint]: "calc(46px + var(--shell-nav-extra-height, 0px))",
    },
  },
});
